import {
  AlertCircle,
  Check,
  CheckCircle2,
  Hash,
  Instagram,
  MessageSquare,
  MoreVertical,
  Pause,
  Pencil,
  Play,
  Plus,
  Reply,
  Send,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import AutoDmService, {
  AutoDmMapping,
  AutoDmSettings,
  InstagramAuthStatusResponse,
} from "../services/autoDmService";
import useToastStore from "../store/useToastStore";

const DEFAULT_SETTINGS: AutoDmSettings = {
  enabled: true,
  replyPublicly: true,
  defaultKeywords: ["link", "price"],
  defaultDmMessage: "Hi! 👋 Thanks for your interest! Here's the link you asked for 👇",
  defaultCommentReply: "Thanks for commenting! 🙌 We've just sent you a DM with the link.",
};

/* -------------------------------------------------------------------------- */
/* Keyword chips input                                                         */
/* -------------------------------------------------------------------------- */
function KeywordInput({
  value,
  onChange,
  placeholder,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState("");

  const add = (raw: string) => {
    const t = raw.trim().toLowerCase();
    if (t && !value.includes(t)) onChange([...value, t]);
    setDraft("");
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2 py-1.5 focus-within:ring-2 focus-within:ring-blue-500/40 focus-within:border-blue-500">
      {value.map((kw) => (
        <span key={kw} className="inline-flex items-center gap-1 rounded-md bg-blue-50 text-blue-700 text-sm px-2 py-0.5">
          {kw}
          <button type="button" onClick={() => onChange(value.filter((k) => k !== kw))} className="text-blue-400 hover:text-blue-700" aria-label={`Remove ${kw}`}>
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            add(draft);
          } else if (e.key === "Backspace" && !draft && value.length) {
            onChange(value.slice(0, -1));
          }
        }}
        onBlur={() => draft && add(draft)}
        placeholder={value.length ? "" : placeholder || "Type a keyword and press Enter"}
        className="flex-1 min-w-[120px] border-0 outline-none text-sm py-1 px-1 bg-transparent"
      />
    </div>
  );
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${checked ? "bg-blue-600" : "bg-gray-300"}`}
      aria-pressed={checked}
    >
      <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${checked ? "left-[22px]" : "left-0.5"}`} />
    </button>
  );
}

const Card: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = "" }) => (
  <div className={`bg-white rounded-2xl border border-gray-200 p-5 sm:p-6 ${className}`}>{children}</div>
);

/* -------------------------------------------------------------------------- */
/* Page                                                                        */
/* -------------------------------------------------------------------------- */
const AutoDmPage = () => {
  const { showToast } = useToastStore();

  // Connection
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [authUrl, setAuthUrl] = useState("");
  const [authStatus, setAuthStatus] = useState<InstagramAuthStatusResponse | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);

  // Settings
  const [settings, setSettings] = useState<AutoDmSettings>(DEFAULT_SETTINGS);
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  // Rules
  const [mappings, setMappings] = useState<AutoDmMapping[]>([]);
  const [isLoadingMappings, setIsLoadingMappings] = useState(false);
  const [isSavingMapping, setIsSavingMapping] = useState(false);
  const [deletingMappingId, setDeletingMappingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    postIdOrUrl: "",
    keywords: [] as string[],
    dmMessage: "",
    commentReply: "",
  });

  // Inline edit of an existing rule — `editingId` is the rule being edited.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    postIdOrUrl: "",
    keywords: [] as string[],
    dmMessage: "",
    commentReply: "",
  });
  const [isUpdatingMapping, setIsUpdatingMapping] = useState(false);
  const [togglingMappingId, setTogglingMappingId] = useState<string | null>(null);

  const isConnected = Boolean(authStatus?.connected);

  useEffect(() => {
    (async () => {
      setIsLoadingStatus(true);
      setStatusError(null);
      try {
        const [status, auth] = await Promise.all([
          AutoDmService.getInstagramAuthStatus(),
          AutoDmService.getInstagramAuthUrl(),
        ]);
        setAuthStatus(status);
        setAuthUrl(auth.url);
      } catch (error) {
        setStatusError(error instanceof Error ? error.message : "Failed to load Instagram connection");
      } finally {
        setIsLoadingStatus(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!isConnected) {
      setMappings([]);
      return;
    }
    (async () => {
      setIsLoadingMappings(true);
      try {
        const [rules, s] = await Promise.all([AutoDmService.getMappings(), AutoDmService.getSettings()]);
        setMappings(rules);
        if (s) setSettings(s);
      } catch (error) {
        showToast(error instanceof Error ? error.message : "Failed to load automation", "error");
      } finally {
        setIsLoadingMappings(false);
      }
    })();
  }, [isConnected, showToast]);

  const formatExpiresAt = (value?: string | Date) => {
    if (!value) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  };
  const expiresAt = formatExpiresAt(authStatus?.expiresAt);

  const handleConnect = () => {
    if (!authUrl) return showToast("Instagram auth URL not ready yet", "error");
    setIsConnecting(true);
    window.location.href = authUrl;
  };

  const handleDisconnect = async () => {
    setIsActionsMenuOpen(false);
    setIsDisconnecting(true);
    try {
      await AutoDmService.disconnectInstagram();
      setAuthStatus({ connected: false });
      showToast("Instagram disconnected", "success");
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Failed to disconnect", "error");
    } finally {
      setIsDisconnecting(false);
    }
  };

  const saveSettings = async () => {
    setIsSavingSettings(true);
    try {
      const saved = await AutoDmService.updateSettings(settings);
      setSettings(saved);
      showToast("Automation settings saved", "success");
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Failed to save settings", "error");
    } finally {
      setIsSavingSettings(false);
    }
  };

  const createRule = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form.postIdOrUrl.trim()) return showToast("Enter the reel or post URL", "error");
    setIsSavingMapping(true);
    try {
      const res = await AutoDmService.createMapping({
        postIdOrUrl: form.postIdOrUrl.trim(),
        keywords: form.keywords,
        dmMessage: form.dmMessage.trim() || undefined,
        commentReply: form.commentReply.trim() || undefined,
      });
      setMappings((prev) => [res.mapping, ...prev.filter((m) => m.postIdOrUrl !== res.mapping.postIdOrUrl)]);
      setForm({ postIdOrUrl: "", keywords: [], dmMessage: "", commentReply: "" });
      showToast(res.message || "Rule saved", "success");
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Failed to save rule", "error");
    } finally {
      setIsSavingMapping(false);
    }
  };

  const startEditing = (mapping: AutoDmMapping) => {
    setEditingId(mapping.id);
    setEditForm({
      postIdOrUrl: mapping.postIdOrUrl,
      keywords: mapping.keywords,
      dmMessage: mapping.dmMessage || "",
      commentReply: mapping.commentReply || "",
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditForm({ postIdOrUrl: "", keywords: [], dmMessage: "", commentReply: "" });
  };

  const saveEdit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingId) return;
    if (!editForm.postIdOrUrl.trim()) return showToast("Enter the reel or post URL", "error");
    setIsUpdatingMapping(true);
    try {
      // Empty message fields are sent as "" on purpose — that clears the
      // override so the rule falls back to the default rule's text.
      const res = await AutoDmService.updateMapping(editingId, {
        postIdOrUrl: editForm.postIdOrUrl.trim(),
        keywords: editForm.keywords,
        dmMessage: editForm.dmMessage.trim(),
        commentReply: editForm.commentReply.trim(),
      });
      setMappings((prev) => prev.map((m) => (m.id === editingId ? res.mapping : m)));
      cancelEditing();
      showToast(res.message || "Rule updated", "success");
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Failed to update rule", "error");
    } finally {
      setIsUpdatingMapping(false);
    }
  };

  const toggleRule = async (mapping: AutoDmMapping) => {
    const enable = mapping.status === "PAUSED";
    setTogglingMappingId(mapping.id);
    try {
      const res = await AutoDmService.updateMapping(mapping.id, { enabled: enable });
      setMappings((prev) => prev.map((m) => (m.id === mapping.id ? res.mapping : m)));
      showToast(enable ? "Rule resumed" : "Rule paused", "success");
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Failed to update rule", "error");
    } finally {
      setTogglingMappingId(null);
    }
  };

  const deleteRule = async (id: string) => {
    setDeletingMappingId(id);
    try {
      await AutoDmService.deleteMapping(id);
      setMappings((prev) => prev.filter((m) => m.id !== id));
      if (editingId === id) cancelEditing();
      showToast("Rule deleted", "success");
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Failed to delete rule", "error");
    } finally {
      setDeletingMappingId(null);
    }
  };

  const previewDm = settings.defaultDmMessage || "";

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-5">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-gray-900">Instagram Auto-DM</h1>
          <p className="mt-1 text-sm text-gray-500 max-w-2xl">
            Automatically reply to comments on your Instagram posts and reels and send the commenter a
            direct message — triggered by the keywords you choose.
          </p>
        </div>

        {/* Connection */}
        <Card>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <div className="rounded-xl bg-gradient-to-br from-pink-50 to-rose-50 p-3 text-pink-600 flex-shrink-0">
                <Instagram className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900">
                  {isLoadingStatus
                    ? "Checking Instagram connection…"
                    : isConnected
                      ? `Connected as @${authStatus?.username || "instagram"}`
                      : "Instagram not connected"}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {isConnected
                    ? expiresAt ? `Token valid until ${expiresAt}` : "Business account linked"
                    : "Connect a professional (Business/Creator) Instagram account to enable automation."}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {isConnected ? (
                <>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-50 text-green-700 text-sm font-medium">
                    <CheckCircle2 className="w-4 h-4" />
                    Connected
                  </span>
                  <div className="relative">
                    <button
                      onClick={() => setIsActionsMenuOpen((p) => !p)}
                      className="inline-flex items-center justify-center h-9 w-9 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                      aria-label="Instagram actions"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>
                    {isActionsMenuOpen && (
                      <div className="absolute right-0 top-11 z-10 w-40 rounded-lg border border-gray-200 bg-white shadow-lg py-1">
                        <button
                          onClick={handleDisconnect}
                          disabled={isDisconnecting}
                          className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 disabled:opacity-60"
                        >
                          {isDisconnecting ? "Disconnecting…" : "Disconnect"}
                        </button>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <button
                  onClick={handleConnect}
                  disabled={isConnecting || isLoadingStatus || !authUrl}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-60"
                >
                  <Instagram className="w-4 h-4" />
                  {isConnecting ? "Connecting…" : "Connect Instagram"}
                </button>
              )}
            </div>
          </div>
          {statusError && (
            <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-800">{statusError}</p>
            </div>
          )}
        </Card>

        {isConnected && (
          <>
            {/* Default rule — applies to every post/reel */}
            <Card>
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h2 className="text-sm font-semibold text-gray-900">Default rule</h2>
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${settings.enabled ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                      {settings.enabled ? "On" : "Off"}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">Runs on every post &amp; reel. Add a post rule below to override it for a specific one.</p>
                  <p className="text-[11px] text-gray-400 mt-1">
                    This switch is the master control — turning it off stops all Instagram automation, including your post rules.
                  </p>
                </div>
                <Toggle checked={settings.enabled} onChange={(v) => setSettings((s) => ({ ...s, enabled: v }))} />
              </div>

              <div className={`space-y-4 ${settings.enabled ? "" : "opacity-50 pointer-events-none"}`}>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Trigger keywords</label>
                  <KeywordInput
                    value={settings.defaultKeywords}
                    onChange={(v) => setSettings((s) => ({ ...s, defaultKeywords: v }))}
                    placeholder="e.g. link, price, buy — press Enter"
                  />
                  <p className="mt-1 text-[11px] text-gray-400">A DM is sent only when a comment contains one of these words. Leave empty to reply to every comment.</p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">DM message</label>
                  <textarea
                    rows={3}
                    value={settings.defaultDmMessage}
                    onChange={(e) => setSettings((s) => ({ ...s, defaultDmMessage: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500"
                    placeholder="Hi! 👋 Here's our store: https://yourstore.mart-24.com"
                  />
                  <p className="mt-1 text-[11px] text-gray-400">Sent exactly as written — just paste your link right into the message.</p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Public comment reply</label>
                  <input
                    value={settings.defaultCommentReply}
                    onChange={(e) => setSettings((s) => ({ ...s, defaultCommentReply: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500"
                    placeholder="Thanks for commenting! Check your DM 🙌"
                  />
                </div>

                <label className="flex items-center gap-2.5">
                  <Toggle checked={settings.replyPublicly} onChange={(v) => setSettings((s) => ({ ...s, replyPublicly: v }))} />
                  <span className="text-sm text-gray-700">Also reply publicly to the comment before sending the DM</span>
                </label>
              </div>

              {/* Outside the dimmed block on purpose: switching the automation off
                  greys out the fields, but Save must stay clickable or the "off"
                  state can never be persisted. */}
              <div className="pt-4">
                <button
                  onClick={saveSettings}
                  disabled={isSavingSettings}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-gray-900 text-white rounded-lg text-sm font-semibold hover:bg-gray-800 disabled:opacity-60"
                >
                  <Check className="w-4 h-4" />
                  {isSavingSettings ? "Saving…" : "Save default rule"}
                </button>
              </div>
            </Card>

            {/* DM preview */}
            <Card className="bg-gradient-to-br from-slate-50 to-white">
              <h2 className="text-sm font-semibold text-gray-900 mb-3">Preview</h2>
              <div className="space-y-2 max-w-sm">
                <div className="flex items-start gap-2">
                  <div className="w-7 h-7 rounded-full bg-gray-200 flex-shrink-0" />
                  <div className="rounded-2xl rounded-tl-sm bg-white border border-gray-200 px-3 py-2 text-sm text-gray-800">
                    {settings.defaultKeywords[0] || "link"} 🙌
                  </div>
                </div>
                <div className="flex items-start gap-2 justify-end">
                  <div className="rounded-2xl rounded-tr-sm bg-blue-600 text-white px-3 py-2 text-sm max-w-[85%] whitespace-pre-wrap break-words">
                    {previewDm || DEFAULT_SETTINGS.defaultDmMessage}
                  </div>
                </div>
                <p className="text-[11px] text-gray-400 text-center pt-1">Example DM sent to the commenter</p>
              </div>
            </Card>

            {/* Per-reel/post rules */}
            <Card>
              <h2 className="text-sm font-semibold text-gray-900">Post &amp; reel rules</h2>
              <p className="text-xs text-gray-500 mt-0.5 mb-4">
                Target a specific post or reel with its own keywords and message. Overrides the default rule above.
              </p>

              <form onSubmit={createRule} className="space-y-4 rounded-xl bg-gray-50 p-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Post or reel URL</label>
                  <input
                    value={form.postIdOrUrl}
                    onChange={(e) => setForm((f) => ({ ...f, postIdOrUrl: e.target.value }))}
                    placeholder="https://www.instagram.com/reel/..."
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500"
                  />
                  <p className="mt-1 text-[11px] text-gray-400">The post or reel this rule runs on. Everything below is optional — leave a field empty to use the default rule's.</p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Trigger keywords</label>
                  <KeywordInput
                    value={form.keywords}
                    onChange={(v) => setForm((f) => ({ ...f, keywords: v }))}
                    placeholder="e.g. link, price, buy — press Enter"
                  />
                  <p className="mt-1 text-[11px] text-gray-400">A DM is sent only when a comment contains one of these words. Leave empty to reply to every comment.</p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">DM message</label>
                  <textarea
                    rows={3}
                    value={form.dmMessage}
                    onChange={(e) => setForm((f) => ({ ...f, dmMessage: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500"
                    placeholder="Hi! 👋 Here's our store: https://yourstore.mart-24.com"
                  />
                  <p className="mt-1 text-[11px] text-gray-400">Sent exactly as written — just paste your link right into the message.</p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Public comment reply</label>
                  <input
                    value={form.commentReply}
                    onChange={(e) => setForm((f) => ({ ...f, commentReply: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500"
                    placeholder="Thanks for commenting! Check your DM 🙌"
                  />
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isSavingMapping}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-60"
                  >
                    <Plus className="w-4 h-4" />
                    {isSavingMapping ? "Saving…" : "Add rule"}
                  </button>
                </div>
              </form>

              <div className="mt-4">
                {isLoadingMappings ? (
                  <div className="py-10 text-center text-sm text-gray-400">Loading rules…</div>
                ) : mappings.length === 0 ? (
                  <div className="py-10 text-center">
                    <MessageSquare className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No post-specific rules yet. Your default rule runs on every post.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100 border-t border-gray-100">
                    {mappings.map((m) => (
                      editingId === m.id ? (
                        <form key={m.id} onSubmit={saveEdit} className="py-3.5 space-y-4">
                          <div className="flex items-center justify-between gap-3">
                            <h3 className="text-sm font-semibold text-gray-900">Edit rule</h3>
                            <span className="text-[10px] font-semibold tracking-wider text-gray-400 uppercase">{m.contentType}</span>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1.5">Post or reel URL</label>
                            <input
                              value={editForm.postIdOrUrl}
                              onChange={(e) => setEditForm((f) => ({ ...f, postIdOrUrl: e.target.value }))}
                              placeholder="https://www.instagram.com/reel/..."
                              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1.5">Trigger keywords</label>
                            <KeywordInput
                              value={editForm.keywords}
                              onChange={(v) => setEditForm((f) => ({ ...f, keywords: v }))}
                              placeholder="e.g. link, price, buy — press Enter"
                            />
                            <p className="mt-1 text-[11px] text-gray-400">Leave empty to use the default rule's keywords.</p>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1.5">DM message</label>
                            <textarea
                              rows={3}
                              value={editForm.dmMessage}
                              onChange={(e) => setEditForm((f) => ({ ...f, dmMessage: e.target.value }))}
                              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500"
                              placeholder="Hi! 👋 Here's our store: https://yourstore.mart-24.com"
                            />
                            <p className="mt-1 text-[11px] text-gray-400">Clear this field to fall back to the default DM message.</p>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1.5">Public comment reply</label>
                            <input
                              value={editForm.commentReply}
                              onChange={(e) => setEditForm((f) => ({ ...f, commentReply: e.target.value }))}
                              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500"
                              placeholder="Thanks for commenting! Check your DM 🙌"
                            />
                          </div>

                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={cancelEditing}
                              disabled={isUpdatingMapping}
                              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-gray-700 text-sm font-semibold hover:bg-gray-50 disabled:opacity-60"
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              disabled={isUpdatingMapping}
                              className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-60"
                            >
                              <Check className="w-4 h-4" />
                              {isUpdatingMapping ? "Saving…" : "Save changes"}
                            </button>
                          </div>
                        </form>
                      ) : (
                      <div key={m.id} className="py-3.5 flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-[10px] font-semibold tracking-wider text-gray-400 uppercase">{m.contentType}</span>
                            {m.status === "PAUSED" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">Paused</span>}
                          </div>
                          <a href={m.postIdOrUrl} target="_blank" rel="noreferrer" className="text-sm font-medium text-gray-900 hover:text-blue-600 break-all inline-flex items-center gap-1">
                            {m.postIdOrUrl}
                          </a>
                          <div className="mt-1.5 flex flex-wrap gap-1.5">
                            {m.keywords.map((kw) => (
                              <span key={kw} className="inline-flex items-center gap-1 rounded-md bg-blue-50 text-blue-700 text-[11px] px-1.5 py-0.5">
                                <Hash className="w-2.5 h-2.5" />{kw}
                              </span>
                            ))}
                            {m.keywords.length === 0 && <span className="text-[11px] text-gray-400">(default keywords)</span>}
                          </div>
                          <p className="mt-1.5 text-xs text-gray-500 break-words">
                            {m.dmMessage ? `DM: “${m.dmMessage}”` : "Uses the default DM message"}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => startEditing(m)}
                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                            title="Edit rule"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => toggleRule(m)}
                            disabled={togglingMappingId === m.id}
                            className="p-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg disabled:opacity-60"
                            title={m.status === "PAUSED" ? "Resume rule" : "Pause rule"}
                          >
                            {m.status === "PAUSED" ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => deleteRule(m.id)}
                            disabled={deletingMappingId === m.id}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-60"
                            title="Delete rule"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      )
                    ))}
                  </div>
                )}
              </div>
            </Card>
          </>
        )}

        {/* How it works — reference, kept at the bottom */}
        <Card>
          <h2 className="text-sm font-semibold text-gray-900 mb-4">How it works</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { icon: Hash, title: "1. Customer comments", body: "Someone comments a keyword (e.g. “link”, “price”) on your post or reel." },
              { icon: Reply, title: "2. We reply publicly", body: "Your account replies to their comment letting them know a DM is on the way." },
              { icon: Send, title: "3. We send the DM", body: "The commenter receives your message exactly as you wrote it — link included." },
            ].map((s) => (
              <div key={s.title} className="rounded-xl bg-gray-50 p-4">
                <div className="w-9 h-9 rounded-lg bg-white border border-gray-200 flex items-center justify-center text-blue-600 mb-3">
                  <s.icon className="w-4 h-4" />
                </div>
                <p className="text-sm font-semibold text-gray-900">{s.title}</p>
                <p className="mt-1 text-xs text-gray-500 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AutoDmPage;
